import React, { useState, useEffect, useCallback, useRef } from "react";
import { UserService } from "../../services/userService";
import { Table } from "react-bootstrap";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { CircularProgress } from "../../components/custom";
import { PaginationBar } from "../../components/PaginationBar";
import { Icon } from "@iconify/react";
import { deleteUser } from "../../redux/slices/users";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import AddCSV from "../../components/AddCSV";
import { useAuth } from "../../hooks/useAuth";
import { PATH_DASHBOARD } from "../../routes/paths";
import { api } from "../../api/axios";
import { useForm } from "react-hook-form";
import { AllClasses } from "../../configs/allClasses";

const columns = [
  { header: "", accessor: "select" },
  { header: "Surname", accessor: "lastName" },
  { header: "First Name", accessor: "firstName" },
  { header: "Admission No.", accessor: "admissionNumber" },
  { header: "Class", accessor: "currentClass" },
  { header: "Gender", accessor: "gender" },
  { header: "Status", accessor: "status" },
  { header: "Date Added", accessor: "createdAt" },
  { header: "Action", accessor: "" },
];

const EMPTY_FILTERS = {
  firstName: "",
  lastName: "",
  admissionNumber: "",
  currentClass: "",
  gender: "",
  status: "active",
};

export default function StudentsList() {
  const { user } = useAuth();
  const dispatch = useDispatch();

  // Table state
  const [currentTableData, setCurrentTableData] = useState([]);
  const [multiSelect, setMultiSelect] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [canNextPage, setCanNextPage] = useState(false);
  const [canPreviousPage, setCanPreviousPage] = useState(false);
  const [dataTotal, setDataTotal] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // UI state
  const [CSVOpen, setCSVOpen] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [overlay, setOverlay] = useState(false);
  const [confirmation, setConfirmation] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);

  // Filter state — single source of truth for all backend query params
  const [activeFilters, setActiveFilters] = useState(EMPTY_FILTERS);
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);

  // Track active filter count for badge
  const activeFilterCount = Object.entries(activeFilters).filter(
    ([key, val]) => {
      if (key === "status") return val !== "active" && val !== "";
      return val !== "";
    },
  ).length;

  // ─── Core data fetcher ────────────────────────────────────────────────────
  const getData = useCallback(async (pageNum, limitNum, filters) => {
    try {
      setIsLoading(true);
      const cleanFilters = {};
      Object.entries(filters || EMPTY_FILTERS).forEach(([k, v]) => {
        if (v !== "") cleanFilters[k] = v;
      });
      const result = await UserService.findUsers({
        role: "student",
        limit: limitNum,
        page: pageNum,
        ...cleanFilters,
      });
      const { list, totalPages, currentPage, total, limit } = result.data;
      setCurrentTableData(list);
      setDataTotal(total);
      setPageSize(limit);
      setPageCount(totalPages);
      setPage(currentPage);
      setCanPreviousPage(currentPage > 1);
      setCanNextPage(currentPage + 1 <= totalPages);
    } catch (error) {
      console.error("Error fetching students:", error);
      toast.error("Failed to load students");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    getData(1, pageSize, EMPTY_FILTERS);
  }, []);

  // ─── Filter handlers ──────────────────────────────────────────────────────
  const handleApplyFilters = () => {
    setActiveFilters({ ...draftFilters });
    setFilterOpen(false);
    getData(1, pageSize, draftFilters);
  };

  const handleResetFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    setActiveFilters(EMPTY_FILTERS);
    setFilterOpen(false);
    getData(1, pageSize, EMPTY_FILTERS);
  };

  const handleDraftChange = (key, value) => {
    setDraftFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Quick search — fires as you type (debounced via ref)
  const searchTimer = useRef(null);
  const handleQuickSearch = (e) => {
    const val = e.target.value;
    const updated = { ...activeFilters, firstName: val };
    setActiveFilters(updated);
    setDraftFilters(updated);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      getData(1, pageSize, updated);
    }, 400);
  };

  // ─── Pagination ───────────────────────────────────────────────────────────
  const nextPage = () => {
    const next = page + 1 <= pageCount ? page + 1 : page;
    getData(next, pageSize, activeFilters);
  };
  const previousPage = () => {
    const prev = page - 1 > 0 ? page - 1 : 1;
    getData(prev, pageSize, activeFilters);
  };
  const updatePageSize = (limit) => {
    setPageSize(limit);
    getData(1, limit, activeFilters);
  };

  // ─── Multi-select ─────────────────────────────────────────────────────────
  const multiSelectHandle = (id) => {
    setMultiSelect((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };
  const selectAll = () => {
    if (multiSelect.length === currentTableData.length) {
      setMultiSelect([]);
    } else {
      setMultiSelect(currentTableData.map((s) => s._id));
    }
  };

  // ─── CSV upload ───────────────────────────────────────────────────────────
  const { register, handleSubmit, reset } = useForm();

  const createCsvUsers = useCallback(async () => {
    if (csvData.length) {
      let newStudents = csvData.slice(1);
      setCSVOpen(false);
      setIsLoading(true);
      try {
        for (const student of newStudents) {
          const data = {
            firstName: student[1],
            lastName: student[0],
            middleName: student[2],
            currentClass: student[3],
            admissionNumber: student[4],
            parentPhone: student[5],
            email: student[6],
            gender: student[7],
            role: "student",
            password: `${student[1]}${student[4]}`.toLowerCase(),
          };
          const formData = new FormData();
          formData.append("values", JSON.stringify(data));
          await UserService.createUser(formData);
          toast.success(`${student[0]} ${student[1]}'s account created.`);
        }
      } catch (error) {
        setCSVOpen(false);
        if (error?.response?.data?.message) {
          toast.error(error.response.data.message);
        } else {
          toast.error("Failure creating students from CSV");
        }
        return;
      } finally {
        setIsLoading(false);
      }
      getData(page, pageSize, activeFilters);
    }
  }, [csvData, getData, page, pageSize, activeFilters]);

  // ─── Single deactivate/activate ───────────────────────────────────────────
  const deactivateUser = async (studentId, currentStatus) => {
    try {
      setIsLoading(true);
      const newStatus = currentStatus === "active" ? "inactive" : "active";
      const formData = new FormData();
      formData.append("values", JSON.stringify({ status: newStatus }));
      await UserService.updateUser(studentId, formData);
      toast.success(
        `Student ${newStatus === "active" ? "activated" : "deactivated"} successfully`,
      );
      getData(page, pageSize, activeFilters);
    } catch (error) {
      toast.error("Failed to update student status");
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Bulk actions ─────────────────────────────────────────────────────────
  const getTargets = () =>
    multiSelect.length > 0 ? multiSelect : currentTableData.map((s) => s._id);

  const handleMultiTransfer = async () => {
    const targets = getTargets();
    if (!targets.length) return toast.error("No students to transfer");
    setIsLoading(true);
    try {
      await Promise.all(
        targets.map((studentId) =>
          api.post("/class/transfer", {
            studentId,
            currentClass: user.classHandled,
          }),
        ),
      );
      toast.success(`${targets.length} student(s) transferred successfully`);
      setMultiSelect([]);
      getData(page, pageSize, activeFilters);
    } catch {
      toast.error("Failed to transfer students");
    } finally {
      setIsLoading(false);
    }
  };

  const handleMultiDemote = async () => {
    const targets = getTargets();
    if (!targets.length) return toast.error("No students to demote");
    setIsLoading(true);
    try {
      await Promise.all(
        targets.map((studentId) =>
          api.post("/class/demote", {
            studentId,
            currentClass: user.classHandled,
          }),
        ),
      );
      toast.success(`${targets.length} student(s) demoted successfully`);
      setMultiSelect([]);
      getData(page, pageSize, activeFilters);
    } catch {
      toast.error("Failed to demote students");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeactivate = async () => {
    const targets = getTargets();
    if (!targets.length) return toast.error("No students to deactivate");
    setIsLoading(true);
    try {
      await Promise.all(
        targets.map((studentId) => {
          const fd = new FormData();
          fd.append("values", JSON.stringify({ status: "inactive" }));
          return UserService.updateUser(studentId, fd);
        }),
      );
      toast.success(`${targets.length} student(s) deactivated successfully`);
      setMultiSelect([]);
      getData(page, pageSize, activeFilters);
    } catch {
      toast.error("Failed to deactivate students");
    } finally {
      setIsLoading(false);
    }
  };

  const handleActivation = async () => {
    const targets = getTargets();
    if (!targets.length) return toast.error("No students to activate");
    setIsLoading(true);
    try {
      await Promise.all(
        targets.map((studentId) => {
          const fd = new FormData();
          fd.append("values", JSON.stringify({ status: "active" }));
          return UserService.updateUser(studentId, fd);
        }),
      );
      toast.success(`${targets.length} student(s) activated successfully`);
      setMultiSelect([]);
      getData(page, pageSize, activeFilters);
    } catch {
      toast.error("Failed to activate students");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUser = async (id) => {
    dispatch(deleteUser({ id }))
      .unwrap()
      .then(() => {
        setOverlay(false);
        toast.success("Student account deleted successfully");
        getData(page, pageSize, activeFilters);
      })
      .catch(() => toast.error("Unable to delete student account"));
  };

  const deleteMultiple = async () => {
    const targets = getTargets();
    if (!targets.length) return toast.error("No students to delete");
    setIsLoading(true);
    try {
      await Promise.all(targets.map((id) => api.delete(`/users/delete/${id}`)));
      toast.success(
        `${targets.length} ${targets.length > 1 ? "students" : "student"} deleted successfully`,
      );
      setMultiSelect([]);
      getData(page, pageSize, activeFilters);
    } catch {
      toast.error(
        `Unable to delete ${targets.length > 1 ? "students" : "student"}`,
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Computed display label for a class code
  const getClassName = (code) =>
    AllClasses.find((c) => c.code === code)?.name || code;

  // Active filter chips for display
  const filterChips = Object.entries(activeFilters)
    .filter(([key, val]) => {
      if (key === "status") return val !== "";
      return val !== "";
    })
    .map(([key, val]) => {
      const labels = {
        firstName: "First Name",
        lastName: "Surname",
        admissionNumber: "Admission No.",
        currentClass: "Class",
        gender: "Gender",
        status: "Status",
      };
      let displayVal = val;
      if (key === "currentClass") displayVal = getClassName(val);
      return { key, label: `${labels[key]}: ${displayVal}` };
    });

  const removeChip = (key) => {
    const updated = {
      ...activeFilters,
      [key]: key === "status" ? "" : "",
    };
    setActiveFilters(updated);
    setDraftFilters(updated);
    getData(1, pageSize, updated);
  };

  return (
    <>
      {CSVOpen && (
        <AddCSV
          onClose={() => setCSVOpen(false)}
          setData={setCsvData}
          data={csvData}
          handleSubmit={createCsvUsers}
          nbMessage="Please check your column header. The first row must contain First name, Surname, middle name, class code, admission number, parent phone, email, gender."
          type="results"
        />
      )}

      <Wrapper
        className={`d-flex flex-column py-5 ${isLoading ? "position-relative" : ""}`}
      >
        {isLoading && (
          <div className="loading-overlay">
            <CircularProgress />
          </div>
        )}

        <div className="content-wrapper p-3 mt-5">
          {/* ── Top Bar ─────────────────────────────── */}
          <div className="top-bar">
            <div className="top-bar-left">
              <h2 className="page-title">Students</h2>
              <span className="total-badge">{dataTotal} total</span>
            </div>
            <div className="top-bar-right">
              <button onClick={() => setCSVOpen(true)} className="csv-button">
                <Icon icon="mdi:upload" />
                Upload Students
              </button>
            </div>
          </div>

          {/* ── Search + Filter Row ──────────────────── */}
          <div className="search-filter-row">
            {/* Quick name search */}
            <div className="quick-search-wrap">
              <Icon icon="circum:search" className="search-icon" />
              <input
                type="text"
                className="quick-search-input"
                placeholder="Search by first name…"
                value={activeFilters.firstName}
                onChange={handleQuickSearch}
                id="quick-search"
              />
              {activeFilters.firstName && (
                <button
                  className="clear-btn"
                  onClick={() => {
                    const updated = { ...activeFilters, firstName: "" };
                    setActiveFilters(updated);
                    setDraftFilters(updated);
                    getData(1, pageSize, updated);
                  }}
                >
                  <Icon icon="mdi:close" />
                </button>
              )}
            </div>

            {/* Advanced filter toggle */}
            <button
              className={`filter-toggle-btn ${filterOpen ? "active" : ""}`}
              onClick={() => {
                setDraftFilters({ ...activeFilters });
                setFilterOpen((v) => !v);
              }}
              id="filter-toggle"
            >
              <Icon icon="mdi:filter-variant" />
              Filters
              {activeFilterCount > 0 && (
                <span className="filter-badge">{activeFilterCount}</span>
              )}
            </button>
          </div>

          {/* ── Filter Panel ─────────────────────────── */}
          {filterOpen && (
            <div className="filter-panel">
              <div className="filter-grid">
                <div className="filter-field">
                  <label>First Name</label>
                  <input
                    type="text"
                    placeholder="e.g. Aisha"
                    value={draftFilters.firstName}
                    onChange={(e) =>
                      handleDraftChange("firstName", e.target.value)
                    }
                    id="filter-firstName"
                  />
                </div>
                <div className="filter-field">
                  <label>Surname</label>
                  <input
                    type="text"
                    placeholder="e.g. Babatunde"
                    value={draftFilters.lastName}
                    onChange={(e) =>
                      handleDraftChange("lastName", e.target.value)
                    }
                    id="filter-lastName"
                  />
                </div>
                <div className="filter-field">
                  <label>Admission No.</label>
                  <input
                    type="text"
                    placeholder="e.g. FGS/2024/001"
                    value={draftFilters.admissionNumber}
                    onChange={(e) =>
                      handleDraftChange("admissionNumber", e.target.value)
                    }
                    id="filter-admissionNumber"
                  />
                </div>
                <div className="filter-field">
                  <label>Class</label>
                  <select
                    value={draftFilters.currentClass}
                    onChange={(e) =>
                      handleDraftChange("currentClass", e.target.value)
                    }
                    id="filter-class"
                  >
                    <option value="">All Classes</option>
                    {AllClasses.map((cls) => (
                      <option key={cls.code} value={cls.code}>
                        {cls.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="filter-field">
                  <label>Gender</label>
                  <select
                    value={draftFilters.gender}
                    onChange={(e) =>
                      handleDraftChange("gender", e.target.value)
                    }
                    id="filter-gender"
                  >
                    <option value="">All</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>
                <div className="filter-field">
                  <label>Status</label>
                  <select
                    value={draftFilters.status}
                    onChange={(e) =>
                      handleDraftChange("status", e.target.value)
                    }
                    id="filter-status"
                  >
                    <option value="">All</option>
                    <option value="active">Active</option>
                    <option value="inactive">Deactivated</option>
                  </select>
                </div>
              </div>
              <div className="filter-actions">
                <button
                  className="reset-btn"
                  onClick={handleResetFilters}
                  id="reset-filters"
                >
                  <Icon icon="mdi:refresh" /> Clear All
                </button>
                <button
                  className="apply-btn"
                  onClick={handleApplyFilters}
                  id="apply-filters"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          )}

          {/* ── Active Filter Chips ───────────────────── */}
          {filterChips.length > 0 && (
            <div className="chip-row">
              {filterChips.map((chip) => (
                <span key={chip.key} className="chip">
                  {chip.label}
                  <button onClick={() => removeChip(chip.key)}>
                    <Icon icon="mdi:close-circle" />
                  </button>
                </span>
              ))}
              <button className="chip-clear-all" onClick={handleResetFilters}>
                Clear all
              </button>
            </div>
          )}

          {/* ── Bulk Action Bar ──────────────────────── */}
          {currentTableData.length > 0 && (
            <div className="bulk-bar">
              <div className="bulk-left">
                <label className="select-all-label">
                  <input
                    type="checkbox"
                    checked={
                      multiSelect.length === currentTableData.length &&
                      currentTableData.length > 0
                    }
                    onChange={selectAll}
                    id="select-all"
                  />
                  {multiSelect.length > 0
                    ? `${multiSelect.length} selected`
                    : "Select all"}
                </label>
              </div>
              <div className="bulk-actions">
                <button
                  className="bulk-btn transfer"
                  onClick={handleMultiTransfer}
                  title={`Transfer ${multiSelect.length > 0 ? multiSelect.length + " selected" : "all on page"}`}
                >
                  <Icon icon="mdi:transfer" />
                  Transfer{" "}
                  {multiSelect.length > 0 ? `(${multiSelect.length})` : "All"}
                </button>
                <button className="bulk-btn demote" onClick={handleMultiDemote}>
                  <Icon icon="mdi:arrow-down-circle-outline" />
                  Demote{" "}
                  {multiSelect.length > 0 ? `(${multiSelect.length})` : "All"}
                </button>
                {activeFilters.status !== "inactive" && (
                  <button
                    className="bulk-btn deactivate"
                    onClick={handleDeactivate}
                  >
                    <Icon icon="mdi:account-off-outline" />
                    Deactivate{" "}
                    {multiSelect.length > 0 ? `(${multiSelect.length})` : "All"}
                  </button>
                )}
                {activeFilters.status === "inactive" && (
                  <button
                    className="bulk-btn activate"
                    onClick={handleActivation}
                  >
                    <Icon icon="mdi:account-check-outline" />
                    Activate{" "}
                    {multiSelect.length > 0 ? `(${multiSelect.length})` : "All"}
                  </button>
                )}
                {multiSelect.length > 0 && (
                  <button
                    className="bulk-btn delete"
                    onClick={() => setConfirmation(true)}
                  >
                    <Icon icon="mdi:delete-outline" />
                    Delete ({multiSelect.length})
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ── Table ───────────────────────────────── */}
          {currentTableData.length > 0 ? (
            <div className="table-wrapper">
              <Table className="students-table">
                <thead>
                  <tr>
                    {columns.map((col, i) => (
                      <th key={i}>{col.header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentTableData.map((row, i) => (
                    <tr key={i}>
                      {columns.map((cell, idx) => {
                        if (cell.accessor === "select") {
                          return (
                            <td key={idx} className="col-cb">
                              <input
                                type="checkbox"
                                onChange={() => multiSelectHandle(row._id)}
                                checked={multiSelect.includes(row._id)}
                              />
                            </td>
                          );
                        }
                        if (cell.accessor === "lastName") {
                          return (
                            <td key={idx} className="col-name">
                              {row.lastName}
                            </td>
                          );
                        }
                        if (cell.accessor === "firstName") {
                          return (
                            <td key={idx} className="col-name">
                              {row.firstName}
                            </td>
                          );
                        }
                        if (cell.accessor === "admissionNumber") {
                          return (
                            <td key={idx} className="col-id">
                              {row.admissionNumber}
                            </td>
                          );
                        }
                        if (cell.accessor === "currentClass") {
                          return (
                            <td key={idx}>
                              <span className="class-badge">
                                {getClassName(row.currentClass)}
                              </span>
                            </td>
                          );
                        }
                        if (cell.accessor === "gender") {
                          return (
                            <td key={idx}>
                              <span className={`gender-badge ${row.gender}`}>
                                {row.gender}
                              </span>
                            </td>
                          );
                        }
                        if (cell.accessor === "status") {
                          return (
                            <td key={idx}>
                              <span className={`status-badge ${row.status}`}>
                                {row.status}
                              </span>
                            </td>
                          );
                        }
                        if (cell.accessor === "createdAt") {
                          return (
                            <td key={idx} className="col-date">
                              {new Date(row.createdAt).toLocaleDateString(
                                "en-GB",
                                {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                },
                              )}
                            </td>
                          );
                        }
                        if (cell.accessor === "") {
                          return (
                            <td key={idx} className="col-actions">
                              <Link
                                to={`${PATH_DASHBOARD.admin.studentInfo}/${row._id}`}
                              >
                                <button className="action-view">View</button>
                              </Link>
                              <Link
                                to={`${PATH_DASHBOARD.admin.editStudent}/${row._id}`}
                              >
                                <button className="action-edit">Edit</button>
                              </Link>
                              <button
                                onClick={() =>
                                  deactivateUser(row._id, row.status)
                                }
                                className={
                                  row.status === "inactive"
                                    ? "action-activate"
                                    : "action-deactivate"
                                }
                              >
                                {row.status === "active"
                                  ? "Deactivate"
                                  : "Activate"}
                              </button>
                            </td>
                          );
                        }
                        return <td key={idx}>{row[cell.accessor]}</td>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </Table>

              <PaginationBar
                canNextPage={canNextPage}
                canPreviousPage={canPreviousPage}
                currentPage={page}
                nextPage={nextPage}
                previousPage={previousPage}
                pageCount={pageCount}
                pageSize={pageSize}
                updatePageSize={updatePageSize}
              />
            </div>
          ) : (
            !isLoading && (
              <div className="empty-state">
                <Icon
                  icon="mdi:account-school-outline"
                  className="empty-icon"
                />
                <h4>No students found</h4>
                <p>
                  {filterChips.length > 0
                    ? "Try adjusting or clearing your filters."
                    : "Upload students using the button above."}
                </p>
                {filterChips.length > 0 ? (
                  <button className="apply-btn" onClick={handleResetFilters}>
                    Clear filters
                  </button>
                ) : (
                  <button
                    className="csv-button"
                    onClick={() => setCSVOpen(true)}
                  >
                    Upload Students
                  </button>
                )}
              </div>
            )
          )}
        </div>

        {/* ── Delete single overlay ─────────────── */}
        {overlay && (
          <div className="modal-overlay">
            <div className="modal-box">
              <p>Are you sure you want to delete this student profile?</p>
              <div className="modal-btns">
                <button
                  className="modal-yes"
                  onClick={() => handleDeleteUser(deleteId)}
                >
                  Yes, delete
                </button>
                <button className="modal-no" onClick={() => setOverlay(false)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Delete multiple overlay ───────────── */}
        {confirmation && (
          <div className="modal-overlay">
            <div className="modal-box">
              <p>
                Are you sure you want to delete{" "}
                <strong>
                  {multiSelect.length}{" "}
                  {multiSelect.length > 1 ? "students'" : "student's"}
                </strong>{" "}
                profile?
              </p>
              <div className="modal-btns">
                <button
                  className="modal-yes"
                  onClick={() => {
                    deleteMultiple();
                    setConfirmation(false);
                  }}
                >
                  Yes, delete
                </button>
                <button
                  className="modal-no"
                  onClick={() => setConfirmation(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </Wrapper>
    </>
  );
}

const Wrapper = styled.div`
  padding-left: 32px;
  padding-right: 32px;
  background-color: #f5f5f5 !important;

  @media screen and (max-width: 1100px) {
    padding-left: 16px;
    padding-right: 16px;
  }

  /* ── Loading overlay ── */
  .loading-overlay {
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    border-radius: 15px;
  }

  /* ── Content card ── */
  .content-wrapper {
    background-color: white;
    border-radius: 15px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.07);
  }

  /* ── Top bar ── */
  .top-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 20px;
  }
  .top-bar-left {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .page-title {
    font-size: 20px;
    font-weight: 700;
    margin: 0;
    color: #1a1a2e;
  }
  .total-badge {
    background: #eef2ff;
    color: #4361ee;
    font-size: 12px;
    font-weight: 600;
    padding: 3px 10px;
    border-radius: 20px;
  }
  .top-bar-right {
    display: flex;
    gap: 10px;
  }

  /* ── CSV button ── */
  .csv-button {
    display: flex;
    align-items: center;
    gap: 6px;
    background: #4361ee;
    color: white;
    border: none;
    border-radius: 8px;
    padding: 9px 18px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;
    &:hover {
      background: #3a0ca3;
    }
  }

  /* ── Search + Filter Row ── */
  .search-filter-row {
    display: flex;
    gap: 10px;
    align-items: center;
    margin-bottom: 12px;
    flex-wrap: wrap;
  }
  .quick-search-wrap {
    position: relative;
    flex: 1;
    min-width: 200px;
    max-width: 380px;
  }
  .search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #9ca3af;
    font-size: 18px;
    pointer-events: none;
  }
  .quick-search-input {
    width: 100%;
    padding: 10px 36px 10px 38px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 13px;
    color: #374151;
    background: #fafafa;
    transition:
      border 0.2s,
      box-shadow 0.2s;
    &:focus {
      outline: none;
      border-color: #4361ee;
      box-shadow: 0 0 0 3px rgba(67, 97, 238, 0.12);
    }
    &::placeholder {
      color: #9ca3af;
    }
  }
  .clear-btn {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: #9ca3af;
    cursor: pointer;
    padding: 0;
    display: flex;
    font-size: 16px;
    &:hover {
      color: #ef4444;
    }
  }

  /* ── Filter toggle button ── */
  .filter-toggle-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: white;
    padding: 10px 16px;
    font-size: 13px;
    font-weight: 600;
    color: #374151;
    cursor: pointer;
    position: relative;
    transition:
      border 0.2s,
      background 0.2s;
    &.active,
    &:hover {
      border-color: #4361ee;
      color: #4361ee;
      background: #eef2ff;
    }
  }
  .filter-badge {
    background: #4361ee;
    color: white;
    border-radius: 50%;
    font-size: 10px;
    width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 2px;
  }

  /* ── Filter Panel ── */
  .filter-panel {
    background: #f8f9ff;
    border: 1px solid #e0e7ff;
    border-radius: 10px;
    padding: 18px;
    margin-bottom: 14px;
    animation: slideDown 0.2s ease;
  }
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  .filter-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 14px;
    margin-bottom: 14px;
  }
  .filter-field {
    display: flex;
    flex-direction: column;
    gap: 5px;
    label {
      font-size: 11px;
      font-weight: 600;
      color: #6b7280;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    input,
    select {
      border: 1px solid #d1d5db;
      border-radius: 7px;
      padding: 8px 10px;
      font-size: 13px;
      color: #374151;
      background: white;
      transition: border 0.2s;
      &:focus {
        outline: none;
        border-color: #4361ee;
        box-shadow: 0 0 0 3px rgba(67, 97, 238, 0.1);
      }
    }
  }
  .filter-actions {
    display: flex;
    gap: 10px;
    justify-content: flex-end;
    align-items: center;
    border-top: 1px solid #e0e7ff;
    padding-top: 14px;
  }
  .reset-btn {
    display: flex;
    align-items: center;
    gap: 5px;
    background: none;
    border: 1px solid #d1d5db;
    border-radius: 7px;
    padding: 8px 16px;
    font-size: 13px;
    color: #6b7280;
    cursor: pointer;
    &:hover {
      border-color: #ef4444;
      color: #ef4444;
    }
  }
  .apply-btn {
    background: #4361ee;
    color: white;
    border: none;
    border-radius: 7px;
    padding: 8px 22px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    &:hover {
      background: #3a0ca3;
    }
  }

  /* ── Chips ── */
  .chip-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 12px;
    align-items: center;
  }
  .chip {
    display: flex;
    align-items: center;
    gap: 5px;
    background: #eef2ff;
    color: #4361ee;
    border-radius: 20px;
    padding: 4px 12px;
    font-size: 12px;
    font-weight: 500;
    button {
      background: none;
      border: none;
      color: #4361ee;
      cursor: pointer;
      padding: 0;
      display: flex;
      font-size: 14px;
      &:hover {
        color: #ef4444;
      }
    }
  }
  .chip-clear-all {
    background: none;
    border: none;
    color: #ef4444;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 20px;
    &:hover {
      background: #fee2e2;
    }
  }

  /* ── Bulk action bar ── */
  .bulk-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    padding: 10px 0;
    border-top: 1px solid #f3f4f6;
    border-bottom: 1px solid #f3f4f6;
    margin-bottom: 8px;
  }
  .select-all-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 500;
    color: #374151;
    cursor: pointer;
    input[type="checkbox"] {
      width: 15px;
      height: 15px;
      cursor: pointer;
    }
  }
  .bulk-actions {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
  }
  .bulk-btn {
    display: flex;
    align-items: center;
    gap: 5px;
    border-radius: 20px;
    padding: 5px 14px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    border: 1px solid;
    &.transfer {
      color: #4361ee;
      border-color: #4361ee;
      background: white;
      &:hover {
        background: #4361ee;
        color: white;
      }
    }
    &.demote {
      color: #374151;
      border-color: #d1d5db;
      background: white;
      &:hover {
        background: #374151;
        color: white;
        border-color: #374151;
      }
    }
    &.deactivate {
      color: #f59e0b;
      border-color: #f59e0b;
      background: white;
      &:hover {
        background: #f59e0b;
        color: white;
      }
    }
    &.activate {
      color: #10b981;
      border-color: #10b981;
      background: white;
      &:hover {
        background: #10b981;
        color: white;
      }
    }
    &.delete {
      color: #ef4444;
      border-color: #ef4444;
      background: white;
      &:hover {
        background: #ef4444;
        color: white;
      }
    }
  }

  /* ── Table ── */
  .table-wrapper {
    overflow-x: auto;
  }
  .students-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    th {
      background: #f9fafb;
      color: #6b7280;
      font-weight: 600;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      padding: 10px 12px;
      border-bottom: 1px solid #e5e7eb;
      white-space: nowrap;
    }
    td {
      padding: 10px 12px;
      border-bottom: 1px solid #f3f4f6;
      vertical-align: middle;
      color: #374151;
    }
    tbody tr {
      transition: background 0.15s;
      &:hover {
        background: #f8f9ff;
      }
    }
  }
  .col-name {
    text-transform: capitalize;
    font-weight: 500;
    color: #1f2937;
  }
  .col-id {
    font-weight: 700;
    color: #4361ee;
  }
  .col-date {
    white-space: nowrap;
    color: #6b7280;
  }
  .col-cb {
    width: 36px;
    text-align: center;
    input {
      cursor: pointer;
      width: 15px;
      height: 15px;
    }
  }

  /* Badges */
  .class-badge {
    background: #f0fdf4;
    color: #166534;
    border: 1px solid #bbf7d0;
    border-radius: 5px;
    padding: 2px 8px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
  }
  .gender-badge {
    border-radius: 5px;
    padding: 2px 8px;
    font-size: 11px;
    font-weight: 600;
    text-transform: capitalize;
    &.male {
      background: #eff6ff;
      color: #1d4ed8;
    }
    &.female {
      background: #fdf4ff;
      color: #7e22ce;
    }
  }
  .status-badge {
    border-radius: 5px;
    padding: 2px 8px;
    font-size: 11px;
    font-weight: 600;
    text-transform: capitalize;
    &.active {
      background: #f0fdf4;
      color: #15803d;
    }
    &.inactive {
      background: #fef2f2;
      color: #b91c1c;
    }
  }

  /* ── Row action buttons ── */
  .col-actions {
    display: flex;
    gap: 6px;
    align-items: center;
    white-space: nowrap;
  }
  .action-view {
    background: #eef2ff;
    color: #4361ee;
    border: none;
    border-radius: 6px;
    padding: 5px 10px;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    &:hover {
      background: #4361ee;
      color: white;
    }
  }
  .action-edit {
    background: #f3f4f6;
    color: #374151;
    border: none;
    border-radius: 6px;
    padding: 5px 10px;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    &:hover {
      background: #374151;
      color: white;
    }
  }
  .action-deactivate {
    background: #fff7ed;
    color: #c2410c;
    border: none;
    border-radius: 6px;
    padding: 5px 10px;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    &:hover {
      background: #c2410c;
      color: white;
    }
  }
  .action-activate {
    background: #f0fdf4;
    color: #15803d;
    border: none;
    border-radius: 6px;
    padding: 5px 10px;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    &:hover {
      background: #15803d;
      color: white;
    }
  }

  /* ── Empty state ── */
  .empty-state {
    text-align: center;
    padding: 60px 20px;
    color: #9ca3af;
    .empty-icon {
      font-size: 64px;
      margin-bottom: 16px;
      color: #d1d5db;
    }
    h4 {
      font-size: 18px;
      font-weight: 600;
      color: #374151;
      margin-bottom: 8px;
    }
    p {
      font-size: 14px;
      margin-bottom: 20px;
    }
  }

  /* ── Modal ── */
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
  }
  .modal-box {
    background: white;
    border-radius: 12px;
    padding: 28px 32px;
    max-width: 420px;
    width: 90%;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
    p {
      font-size: 15px;
      color: #374151;
      margin-bottom: 20px;
    }
  }
  .modal-btns {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
  }
  .modal-yes {
    background: #ef4444;
    color: white;
    border: none;
    border-radius: 8px;
    padding: 9px 20px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    &:hover {
      background: #b91c1c;
    }
  }
  .modal-no {
    background: #f3f4f6;
    color: #374151;
    border: none;
    border-radius: 8px;
    padding: 9px 20px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    &:hover {
      background: #e5e7eb;
    }
  }
`;
